import React, { useState, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';
import { ProjectStats } from '../types/chat';
import ProjectTokenStatsDisplay from './ProjectTokenStatsDisplay';
import api from '../utils/axios';
import { Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface ProjectStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectTitle: string;
}

const ProjectStatsModal: React.FC<ProjectStatsModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectTitle
}) => {
  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchProjectStats();
    }
  }, [isOpen, projectId]);

  const fetchProjectStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use billing API for detailed token analytics
      const response = await api.get(`/billing/projects/${projectId}/detailed-analytics`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch project stats:', err);
      setError('Failed to load project statistics');
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={onClose}
        ></div>

        {/* Modal content */}
        <div className="inline-block w-full max-w-4xl px-6 pt-5 pb-6 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <BarChart3 className="h-6 w-6 text-blue-500" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Token Analytics</h3>
                <p className="text-sm text-gray-600">{projectTitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-fg-secondary hover:bg-bg-primary-hover rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              <Icon name={IconName.Cross} size="lg" inheritColor />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading statistics...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 mb-2">⚠️ Error</div>
              <p className="text-gray-600">{error}</p>
              <button
                onClick={fetchProjectStats}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : stats ? (
            <div>
              {/* Token Statistics */}
              {stats.token_stats ? (
                <ProjectTokenStatsDisplay 
                  tokenStats={stats.token_stats} 
                  className="border-0 shadow-none"
                />
              ) : (
                <div className="bg-gray-50 p-8 rounded-lg border border-gray-200 text-center">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-lg font-medium text-gray-600 mb-2">No Token Usage Data</h4>
                  <p className="text-gray-500">
                    Start using Brainstorm or Development mode to see token usage analytics.
                  </p>
                </div>
              )}
            </div>
          ) : null}

          {/* Footer */}
          <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectStatsModal;
